// ACE uploads can end their lines with LF, CRLF, or a bare CR (what Excel for
// Mac's plain "CSV" save option writes). As in the analyzer's chunk planner,
// the file's FIRST line break decides: a bare-CR file splits on CR, CRLF or
// LF; any other file splits on LF only, as before, so a stray CR inside a
// quoted cell of an LF/CRLF file is not taken as a row break.

/** True when the first line break in `text` is a bare CR. */
export const isBareCrFile = (text: string): boolean => {
  const i = text.search(/[\r\n]/);
  return i >= 0 && text[i] === '\r' && text[i + 1] !== '\n';
};

export const splitLines = (text: string, bareCr: boolean): string[] =>
  text.split(bareCr ? /\r\n|\r|\n/ : /\r?\n/);

/**
 * Number of line breaks in `text`. In a bare-CR file a CRLF counts once, and
 * `afterCr` says the text read just before this slice ended on a CR, so a
 * CRLF split across two FileReader slices is not counted twice.
 */
export const countLineBreaks = (
  text: string,
  bareCr: boolean,
  afterCr = false,
): number => {
  let count = 0;
  for (let i = 0; i < text.length; i++) {
    if (text[i] === '\n') {
      const followsCr = i === 0 ? afterCr : text[i - 1] === '\r';
      if (!bareCr || !followsCr) count++;
    } else if (bareCr && text[i] === '\r') {
      count++;
    }
  }
  return count;
};

/**
 * Quote-aware split of one CSV line, stopping once field `maxIndex` is read.
 * Same rules as the analyzer's chunk planner (parseCsvLine): a quote toggles
 * quoting anywhere in a field and "" inside quotes is a literal quote.
 */
export const readCsvFields = (line: string, maxIndex = Infinity): string[] => {
  const fields: string[] = [];
  let current = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    if (inQuotes) {
      if (char === '"' && line[i + 1] === '"') {
        current += '"';
        i++;
      } else if (char === '"') {
        inQuotes = false;
      } else {
        current += char;
      }
    } else if (char === '"') {
      inQuotes = true;
    } else if (char === ',') {
      fields.push(current);
      if (fields.length > maxIndex) return fields;
      current = '';
    } else {
      current += char;
    }
  }
  fields.push(current);
  return fields;
};

export type AceUploadFormat = 'es003' | 'cargowise';

const sameHeader = (a: string, b: string): boolean =>
  a.toUpperCase().replace(/[^A-Z0-9]/g, '') ===
  b.toUpperCase().replace(/[^A-Z0-9]/g, '');

/**
 * How the analyzer treats a row, from its HTS (cleaned the way its
 * cleanHtsCode does): a Chapter 1-97 code (or 9801.00.10) is the line's base
 * row; any other Chapter 98 code can be promoted to base when the line has
 * none; a Chapter 99 code is only ever a tariff row.
 */
export const aceRowHtsKind = (rawHts: string): 'base' | 'ch98' | 'ch99' => {
  let str = rawHts.trim();
  if (str.endsWith('.0')) str = str.slice(0, -2);
  let cleaned = str.replace(/\./g, '');
  if (cleaned.length > 0 && cleaned.length % 2 !== 0) cleaned = `0${cleaned}`;
  if (cleaned.startsWith('99')) return 'ch99';
  if (cleaned.startsWith('98') && cleaned !== '98010010') return 'ch98';
  return 'base';
};

interface AceLineGroup {
  entry: string;
  /** Base HTS rows seen, capped at 2 (two or more send the line to unmatched). */
  base: number;
  ch98: boolean;
}

/**
 * Counts what an ACE run is billed, whatever order its rows are in. Mirrors
 * the analyzer's prepare step so the number shown before upload is the number
 * charged:
 *   - one credit per unique Entry Summary Number (CargoWise: Entry No., else
 *     Job Number);
 *   - ES-003 keys normalized as the analyzer does (a 10-character summary
 *     number regains its leading zero, short line numbers are zero-padded),
 *     and rows missing either part are skipped;
 *   - an entry is billed only when one of its lines has exactly one base HTS
 *     row, or none but a Chapter 98 row. A line of orphaned Chapter 99 rows,
 *     or one with two base rows, goes to unmatched rows and is never charged.
 * The header row is the first line whose parsed cells include the required
 * columns, so a title or note above it that mentions them is skipped.
 */
export class AceEntryCounter {
  private header: string[] | null = null;
  private summaryIdx = -1;
  private lineIdx = -1;
  private jobIdx = -1;
  private htsFullIdx = -1;
  private htsIdx = -1;
  private maxIdx = 0;
  // CargoWise: every row is its own line, so every entry is billed.
  private readonly entries = new Set<string>();
  // ES-003: one group per (entry, line).
  private readonly lines = new Map<string, AceLineGroup>();

  constructor(private readonly format: AceUploadFormat) {}

  get headerFound(): boolean {
    return this.header !== null;
  }

  get count(): number {
    if (this.format === 'cargowise') return this.entries.size;
    const billable = new Set<string>();
    this.lines.forEach((group) => {
      if (group.base === 1 || (group.base === 0 && group.ch98)) {
        billable.add(group.entry);
      }
    });
    return billable.size;
  }

  addLine(line: string): void {
    if (!this.header) {
      this.readHeader(line);
      return;
    }

    const fields = readCsvFields(line, this.maxIdx);
    let summary = (fields[this.summaryIdx] || '').trim();

    if (this.format === 'cargowise') {
      const entryKey =
        summary || (this.jobIdx >= 0 ? (fields[this.jobIdx] || '').trim() : '');
      if (entryKey) this.entries.add(entryKey);
      return;
    }

    let lineNumber = (fields[this.lineIdx] || '').trim();
    if (lineNumber.length > 0 && lineNumber.length < 3) {
      lineNumber = lineNumber.padStart(3, '0');
    }
    if (summary.length === 10 && !summary.includes('-'))
      summary = `0${summary}`;
    if (!summary || !lineNumber) return;

    const key = `${summary}\t${lineNumber}`;
    let group = this.lines.get(key);
    if (!group) {
      group = { entry: summary, base: 0, ch98: false };
      this.lines.set(key, group);
    }
    // Same fallback as the analyzer: 'HTS Number - Full', else 'HTS Number'.
    const kind = aceRowHtsKind(
      fields[this.htsFullIdx] || fields[this.htsIdx] || '',
    );
    if (kind === 'base') group.base = Math.min(group.base + 1, 2);
    else if (kind === 'ch98') group.ch98 = true;
  }

  private readHeader(line: string): void {
    // Every header row names an entry column; skip parsing anything else.
    if (!/entry/i.test(line)) return;

    const cells = readCsvFields(line.replace(/^\uFEFF/, '').trim()).map((h) =>
      h.replace(/^\uFEFF/, '').trim(),
    );
    const find = (name: string): number => {
      const exact = cells.indexOf(name);
      return exact >= 0 ? exact : cells.findIndex((h) => sameHeader(h, name));
    };

    if (this.format === 'cargowise') {
      const summaryIdx = find('Entry No.');
      if (summaryIdx < 0) return;
      this.summaryIdx = summaryIdx;
      this.jobIdx = find('Job Number');
    } else {
      const summaryIdx = find('Entry Summary Number');
      const lineIdx = find('Entry Summary Line Number');
      if (summaryIdx < 0 || lineIdx < 0) return;
      this.summaryIdx = summaryIdx;
      this.lineIdx = lineIdx;
      this.htsFullIdx = find('HTS Number - Full');
      this.htsIdx = find('HTS Number');
    }
    this.header = cells;
    this.maxIdx = Math.max(
      this.summaryIdx,
      this.lineIdx,
      this.jobIdx,
      this.htsFullIdx,
      this.htsIdx,
      0,
    );
  }
}
